import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ShellContext } from "@insite/client-framework/Components/IsInShell";
import displayProducts from "@insite/client-framework/Store/Pages/ProductList/Handlers/DisplayProducts";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { mediaOptions } from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import addProductPageFilters from "@store/Store/Pages/ProductList/Handlers/AddProductPageFilters";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import { TYPOGRAPHY } from "@store/theme/design";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import React, { FC, useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    useDynamicLoading = "useDynamicLoading",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.useDynamicLoading]: boolean;
    };
}

const mapStateToProps = (state: StoreApplicationState) => {
    const {
        store: {
            pages: {
                productList: { isMoreLoading },
            },
        },
    } = state;
    const {
        pages: {
            productList: { isLoading },
        },
    } = state;
    return {
        pagination: getProductListDataViewProperty(state, "pagination"),
        isMoreLoading,
        isLoading,
    };
};

const mapDispatchToProps = {
    addProductPageFilters,
    displayProducts,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListPaginationStyles {
    wrapper?: InjectableCss;
    wrapperDynamicLoadingShell?: InjectableCss;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
	button?: ButtonPresentationProps;
    itemsLeft?: InjectableCss;
}

export const paginationStyles: ProductListPaginationStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            flex-direction: column;
            margin: 40px auto 0;
        `,
    },
    wrapperDynamicLoadingShell: {
        css: css`
            position: relative;
            background: #e5e5e5;
            padding: 10px;
        `,
    },
    centeringWrapper: {
        css: css`
            width: 100%;
            height: 100px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
	button: {
		variant: "tertiary",
	},
    itemsLeft: {
        css: css`
        font-size: ${TYPOGRAPHY.size.smaller};
        margin-bottom: 20px;
        `,
    },
};

const styles = paginationStyles;

const ProductListPagination: FC<Props> = ({ pagination, isMoreLoading, isLoading, addProductPageFilters, fields }) => {
    const shellContext = React.useContext(ShellContext);
    const { useDynamicLoading } = fields;

    const currentPage = pagination?.page;
    const resultsCount = pagination?.totalItemCount;
    const resultsPerPage = pagination?.pageSize;

    const finalPageIndex =
        resultsCount && resultsPerPage ? Math.floor((resultsCount - 1) / resultsPerPage) + 1 : undefined;
    const loadMoreDisabled =
        currentPage && finalPageIndex ? currentPage === finalPageIndex || finalPageIndex === 1 : undefined;
    const displayedProducts =
        currentPage && finalPageIndex && resultsPerPage ? resultsPerPage * currentPage : undefined;

    const [isLoadingMore, setIsLoadingMore] = React.useState(true);
    const [loadMoreScrollTopPx, setLoadMoreScrollTopPx] = React.useState(0);

    const loadMore = (newPageIndex: number) => {
        addProductPageFilters({
            page: newPageIndex,
        });
    };
    const initScrollValue = () => {
        const idToLoadMore = document.getElementById("load-more");
        const viewportPosition = idToLoadMore?.getBoundingClientRect().top || 0; // Position within the viewport
        const viewportToDocument = window.scrollY; // Distance between top of viewport and top of document
        const scrollTopValue = viewportPosition + viewportToDocument - 1080;
        setLoadMoreScrollTopPx(scrollTopValue);
        setIsLoadingMore(false);
    };
    const handleScroll = () => {
        const scrollTop = document.body.scrollTop || document.documentElement.scrollTop;
        if (!isLoadingMore && loadMoreScrollTopPx > 0 && scrollTop > loadMoreScrollTopPx) {
            setIsLoadingMore(true);
            setLoadMoreScrollTopPx(0);
            window.removeEventListener("scroll", handleScroll);
            loadMore(pagination!.page + 1);
        }
    };

    useEffect(() => {
        if (!shellContext.isEditing) {
            if (loadMoreScrollTopPx > 0 && !isLoadingMore && !loadMoreDisabled) {
                window.addEventListener("scroll", handleScroll);
                handleScroll();
            }
        }
    }, [loadMoreScrollTopPx, isLoadingMore]);

    useEffect(() => {
        if (!isMoreLoading && useDynamicLoading) {
            initScrollValue();
        }
    }, [isMoreLoading]);

    useEffect(() => {
        if (!isLoading && useDynamicLoading) {
            initScrollValue();
        }
    }, [isLoading]);

    useEffect(() => {
        if (useDynamicLoading) {
            if (typeof window === "undefined") {
                return;
            }
            initScrollValue();
        }
    }, []);

    if (!pagination || !pagination.totalItemCount) {
        return null;
    }

    const notice = shellContext.isEditing && (
        <StyledWrapper {...styles.wrapperDynamicLoadingShell}>
            Important note: Do not use ProductListPagination widget with the LoadMore widget
        </StyledWrapper>
    );

    const loading = isMoreLoading && (
        <StyledWrapper {...styles.centeringWrapper}>
            <LoadingSpinner {...styles.spinner} data-test-selector="productListCardListSpinner" />
        </StyledWrapper>
    );

    if (!useDynamicLoading) {
        return (
            <StyledWrapper {...styles.wrapper}>
                {loading}
                {!isMoreLoading && (
                <> 
                    <StyledWrapper {...styles.itemsLeft}>
                        {!loadMoreDisabled ? 
                            translate("{0} of {1} item(s)",String(displayedProducts), String(resultsCount)) : 
                            translate("{0} item(s)", String(resultsCount))
                        }
                    </StyledWrapper>
                   
                    {!loadMoreDisabled && (
                        <Button
                            {...styles.button}
                            disabled={loadMoreDisabled}
                            onClick={() => loadMore(pagination!.page + 1)}
                            data-test-selector="product-list-load-more"
                        >
                            {translate("Load More")}
                        </Button>
                    )}
                </>
                )}
                {notice}
            </StyledWrapper>
        );
    }
    return (
        <>
            <span id="load-more"></span>
            {loading}
            <>
                {shellContext.isEditing && (
                    <StyledWrapper {...styles.wrapperDynamicLoadingShell}>Dynamic Loading</StyledWrapper>
                )}
                {notice}
            </>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListPagination),
    definition: {
        group: "Product List",
        displayName: "Load More",
        allowedContexts: ["ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.useDynamicLoading,
                displayName: "Use Dynamic Loading",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip: "If unchecked, a button 'Load More' will be displayed to allow for loading more content.",
            },
        ],
    },
};

export default widgetModule;
